/**
 * Notification Hooks
 *
 * Provides access to in-app notifications with real-time polling.
 */

import { trpc } from '@/trpc/client';
import { POLLING_INTERVALS, STALE_TIMES } from '@/lib/constants';
import { useLocationId } from './useLocationId';

/**
 * Get notifications for the current user, filtered by selected location
 */
export function useNotifications(options?: { unreadOnly?: boolean; limit?: number }) {
  const locationId = useLocationId();

  return trpc.notification.list.useQuery(
    { ...options, locationId },
    {
      refetchInterval: POLLING_INTERVALS.FAST,
      staleTime: STALE_TIMES.FAST,
    }
  );
}

/**
 * Get unread notification count, filtered by selected location
 */
export function useUnreadCount() {
  const locationId = useLocationId();

  return trpc.notification.getUnreadCount.useQuery(
    { locationId },
    {
      refetchInterval: POLLING_INTERVALS.FAST,
      staleTime: STALE_TIMES.FAST,
    }
  );
}

/**
 * Notification mutation hooks
 */
export function useNotificationMutations() {
  const utils = trpc.useUtils();

  const markAsReadMutation = trpc.notification.markAsRead.useMutation({
    onSuccess: () => {
      utils.notification.list.invalidate();
      utils.notification.getUnreadCount.invalidate();
    },
  });

  const markAllAsReadMutation = trpc.notification.markAllAsRead.useMutation({
    onSuccess: () => {
      utils.notification.list.invalidate();
      utils.notification.getUnreadCount.invalidate();
    },
  });

  return {
    markAsRead: markAsReadMutation.mutate,
    markAllAsRead: markAllAsReadMutation.mutate,
    isMarkingAsRead: markAsReadMutation.isPending,
    isMarkingAllAsRead: markAllAsReadMutation.isPending,
  };
}
